import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiAcceptInvite, apiRequestPasswordReset } from "@/lib/authApi";
import { useIamLogoutMutation } from "@/graphql/generated";
import {
  useAdminSession,
  type LoginResult,
  type ResendCodeResult,
  type VerifyCodeResult,
} from "@/hooks/useAdminSession";
import { useSessionKeepAlive } from "@/hooks/useSessionKeepAlive";
import {
  AUTH_STORAGE_KEY,
  clearStoredSession,
  persistStoredSession,
  safeParseSession,
  type StoredSession,
} from "@/lib/authStorage";
import {
  getJwtExpiryMs,
  refreshSessionIfNeeded,
  SESSION_REFRESHED_EVENT,
  type SessionRefreshedEventDetail,
} from "@/lib/sessionRefresh";

export type AuthUser = {
  id: string;
  name: string;
  email: string;
  role: "ADMIN" | "OPS" | "SUPPORT" | "UNKNOWN" | (string & {});
  avatar?: string;
  status?: string;
  lastLoginAt?: string;
  createdAt?: string;
};

type AcceptInviteResult =
  | { ok: true }
  | {
      ok: false;
      reason: "INVALID" | "EXPIRED" | "ALREADY_USED" | "UNKNOWN";
      message?: string;
    };

type AuthContextType = {
  isInitializing: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyLoginCode: (
    loginTicket: string,
    code: string,
  ) => Promise<VerifyCodeResult>;
  resendLoginCode: (loginTicket: string) => Promise<ResendCodeResult>;
  logout: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<
    | { ok: true }
    | {
        ok: false;
        reason:
          | "EMAIL_NOT_FOUND"
          | "ACCOUNT_BLOCKED"
          | "NOT_ACTIVATED"
          | "UNKNOWN";
      }
  >;
  acceptInvite: (params: {
    token: string;
    password: string;
  }) => Promise<AcceptInviteResult>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  const iamLogoutMutation = useIamLogoutMutation();

  useSessionKeepAlive({ enabled: !!token && !!user, refreshOnMount: true });

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const existing = safeParseSession(localStorage.getItem(AUTH_STORAGE_KEY));
      if (!existing) {
        if (!cancelled) setIsInitializing(false);
        return;
      }

      const expiryMs = getJwtExpiryMs(existing.token);
      const shouldRefreshBeforeRestore =
        !expiryMs || expiryMs <= Date.now() + 30_000;

      if (shouldRefreshBeforeRestore) {
        const refreshResult = await refreshSessionIfNeeded({ force: true });
        if (cancelled) return;

        if (refreshResult.ok && "accessToken" in refreshResult) {
          setToken(refreshResult.accessToken);
          setUser(existing.user);
        } else {
          clearStoredSession();
          setToken(null);
          setUser(null);
        }
        setIsInitializing(false);
        return;
      }

      if (!cancelled) {
        setToken(existing.token);
        setUser(existing.user);
        setIsInitializing(false);
      }
    }

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleSessionRefreshed = (event: Event) => {
      const detail = (event as CustomEvent<SessionRefreshedEventDetail>).detail;
      if (typeof detail?.accessToken === "string") {
        setToken(detail.accessToken);
      }
    };

    window.addEventListener(SESSION_REFRESHED_EVENT, handleSessionRefreshed);
    return () => {
      window.removeEventListener(
        SESSION_REFRESHED_EVENT,
        handleSessionRefreshed,
      );
    };
  }, []);

  const startSession = useCallback((session: StoredSession) => {
    setToken(session.token);
    setUser(session.user);
    persistStoredSession(session);
  }, []);

  const { login, verifyLoginCode, resendLoginCode } =
    useAdminSession(startSession);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await iamLogoutMutation.mutateAsync({});
    } catch {
      // server-side revocation is best-effort; always clear local session
    } finally {
      setToken(null);
      setUser(null);
      clearStoredSession();
    }
  }, [iamLogoutMutation]);

  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      return await apiRequestPasswordReset(email);
    } catch (e) {
      const err = e as Error & { status?: number; code?: string };
      if (err.code === "ACCOUNT_BLOCKED")
        return { ok: false as const, reason: "ACCOUNT_BLOCKED" as const };
      if (err.code === "NOT_ACTIVATED")
        return { ok: false as const, reason: "NOT_ACTIVATED" as const };
      if (err.status === 404)
        return { ok: false as const, reason: "EMAIL_NOT_FOUND" as const };
      return { ok: false as const, reason: "UNKNOWN" as const };
    }
  }, []);

  const acceptInvite = useCallback(
    async (params: { token: string; password: string }) => {
      try {
        const res = await apiAcceptInvite(params);
        return res.ok
          ? ({ ok: true } as const)
          : ({ ok: false, reason: "UNKNOWN" } as const);
      } catch (e) {
        const err = e as Error & { status?: number; code?: string };
        if (err.code === "INVALID")
          return { ok: false as const, reason: "INVALID" as const };
        if (err.code === "EXPIRED")
          return { ok: false as const, reason: "EXPIRED" as const };
        if (err.code === "ALREADY_USED")
          return { ok: false as const, reason: "ALREADY_USED" as const };
        return {
          ok: false as const,
          reason: "UNKNOWN" as const,
          message: err.message,
        };
      }
    },
    [],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      isInitializing,
      isAuthenticated: !!token && !!user,
      user,
      token,
      login,
      verifyLoginCode,
      resendLoginCode,
      logout,
      requestPasswordReset,
      acceptInvite,
    }),
    [
      isInitializing,
      token,
      user,
      login,
      verifyLoginCode,
      resendLoginCode,
      logout,
      requestPasswordReset,
      acceptInvite,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
